import { CdkDragDrop, moveItemInArray } from '@angular/cdk/drag-drop';
import { Component, inject, OnInit, ViewEncapsulation } from '@angular/core';
import { OwlOptions } from 'ngx-owl-carousel-o';
import { debounceTime, distinctUntilChanged, first, forkJoin, map, Observable, of, switchMap } from 'rxjs';
import { Availability } from '../../../../../models/availability';
import { CustomerProductPrice } from '../../../../../models/price';
import { Product, ProductMultiDimension } from '../../../../../models/product';
import { AuthService } from '../../../../../services/auth/auth.service';
import { AvailabilityService } from '../../../../../services/availability/availability.service';
import { GungFlowService } from '../../../../../services/gung-flow/gung-flow.service';
import { PriceService } from '../../../../../services/price/price.service';
import { ProductListV2ConfigService } from '../../../../../services/product-list-v2-config/product-list-v2-config.service';
import { ProductListConfigService } from '../../../../../services/product-list-config/product-list-config.service';
import { ProductService } from '../../../../../services/products/product.service';
import { GridViewProductCardV2 } from '../../../../../components/product-grid-view-v2/product-grid-view-v2.component';
import { DynamicPageEditorHelperService } from '../../../services/dynamic-page-editor-helper/dynamic-page-editor-helper.service';
import { DynamicPageDcBaseComponent } from '../dynamic-page-dc-base/dynamic-page-dc-base.component';

@Component({
  selector: 'lib-dynamic-page-dc-product-list-carousel',
  standalone: false,
  templateUrl: './dynamic-page-dc-product-list-carousel.component.html',
  styleUrl: './dynamic-page-dc-product-list-carousel.component.scss',
  encapsulation: ViewEncapsulation.None
})
export class DynamicPageDcProductListCarouselComponent extends DynamicPageDcBaseComponent implements OnInit {
  productService = inject(ProductService);
  productListV2ConfigService = inject(ProductListV2ConfigService);
  productListConfig = inject(ProductListConfigService);
  priceService = inject(PriceService);
  availabilityService = inject(AvailabilityService);
  authService = inject(AuthService);
  gungFlowService = inject(GungFlowService);
  dynamicPageEditorHelperService = inject(DynamicPageEditorHelperService);

  elementId = 'product-list-carousel';

  carouselItems: { id: string; component: any; data: GridViewProductCardV2 }[] = [];
  products: Product[];
  productToAdd: string;
  resolvedProductIds: string[] = [];
  carouselAlive = true;
  includeAvailability = false;
  isSales = false;
  isAnonymous = true;
  loadingAssortment = false;

  fields = {
    useProductsIds: true,
    productsIds: [],
    assortmentId: '',
    itemsMobile: 1,
    itemsTablet: 3,
    itemsDesktop: 4
  };

  customOptions: OwlOptions = {
    lazyLoad: true,
    loop: false,
    mouseDrag: false,
    touchDrag: false,
    pullDrag: false,
    dots: false,
    navSpeed: 700,
    navText: ['<i class="fas fa-arrow-left"></i>', '<i class="fas fa-arrow-right"></i>'],
    margin: 10,
    responsive: {},
    nav: true
  };

  search = (searchTerm$: Observable<string>) => {
    return searchTerm$.pipe(
      debounceTime(200),
      distinctUntilChanged(),
      map(term =>
        this.products
          .filter(product => {
            let hasHitAllTerms = true;
            const queryTerms = term.split(' ');
            const terms = this.productListConfig.getSearchTerms(product);
            queryTerms.forEach(queryTerm => {
              const locatedTerm = terms.find(searchTerm => {
                return !!searchTerm && searchTerm.toUpperCase().indexOf(queryTerm.toUpperCase()) >= 0;
              });

              hasHitAllTerms = hasHitAllTerms && !!locatedTerm;
            });
            return hasHitAllTerms;
          })
          .slice(0, 10)
      )
    );
  };

  inputFormatter = (product: Product) => product.id;

  resultFormatter = (product: Product) => product.id + ' - ' + product.name;

  ngOnInit(): void {
    super.ngOnInit();

    this.element.fields.useProductsIds = this.element.fields.useProductsIds ?? true;
    this.element.fields.productsIds = this.element.fields.productsIds ?? [];
    this.element.fields.assortmentId = this.element.fields.assortmentId ?? '';
    this.element.fields.itemsMobile = this.element.fields.itemsMobile ?? 1;
    this.element.fields.itemsTablet = this.element.fields.itemsTablet ?? 3;
    this.element.fields.itemsDesktop = this.element.fields.itemsDesktop ?? 4;
    this.updateCarouselOptions();

    this.productListConfig.getItems().pipe(
      map(products => {
        return products.flatMap(p => {
          if (p.productType === "multiDimension") {
            return (p as ProductMultiDimension).secondaryDimension.map(sd => ({
              ...p,
              id: p.id + sd.id,
              name: p.name + ' - ' + sd.name,
              secondaryDimension: sd
            }));
          }
          return [p];
        });
      })
    ).subscribe(ps => this.products = ps);

    this.authService
      .getRoles()
      .pipe(first())
      .subscribe(roles => {
        this.isSales =
          roles.filter(role => role.toUpperCase() === 'ADMIN' || role.toUpperCase() === 'SALES').length > 0;
        this.isAnonymous = roles.filter(role => role.toUpperCase() === 'ANONYMOUS').length > 0;
      });

    this.gungFlowService.getSelectedFlow().pipe(first()).subscribe(
      flow => { this.includeAvailability = flow.useAvailabilities || flow.requireAvailability; }
    );

    if (this.edit) {
      this.dynamicPageEditorHelperService.productIdsSubject.asObservable().subscribe(() => {
        this.refreshCarousel();
      });
    }

    this.refreshCarousel();
  }

  addProduct(product: Product) {
    this.element.fields.productsIds = [...this.element.fields.productsIds, product.id];
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
    setTimeout(() => this.productToAdd = '');
  }

  deleteProduct(productId: string) {
    this.element.fields.productsIds = this.element.fields.productsIds.filter(id => id !== productId);
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
  }

  dropProduct(event: CdkDragDrop<string[]>) {
    moveItemInArray(this.element.fields.productsIds, event.previousIndex, event.currentIndex);
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
  }

  onSourceChange() {
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
  }

  onAssortmentChange() {
    this.dynamicPageEditorHelperService.productIdsSubject.next(1);
  }

  onItemsChange() {
    this.updateCarouselOptions();
  }

  private refreshCarousel() {
    this.carouselAlive = false;
    this.loadingAssortment = !this.element.fields.useProductsIds && !!this.element.fields.assortmentId;

    this.getResolvedProductIds().pipe(first()).subscribe(ids => {
      this.resolvedProductIds = ids;
      if (!ids || ids.length === 0) {
        this.carouselItems = [];
        this.carouselAlive = true;
        return;
      }
      this.loadCarouselData(ids);
    });
  }

  private getResolvedProductIds(): Observable<string[]> {
    if (this.element.fields.useProductsIds) {
      return of(this.element.fields.productsIds ?? []);
    }
    if (!this.element.fields.assortmentId) {
      return of([]);
    }
    return this.getAssortmentProducts(this.element.fields.assortmentId).pipe(
      first(),
      map(products => products.map(product => product.id))
    );
  }

  private getAssortmentProducts(assortmentId: string): Observable<Product[]> {
    if (this.productListConfig.displayProductsOnVariantLevel === true) {
      return this.productService.getProductsByAssortmentExpanded(assortmentId);
    }
    return this.productService.getProductsByAssortment(assortmentId);
  }

  private loadCarouselData(productIds: string[]) {
    this.productService.getProductsByIds(productIds).pipe(
      first(),
      switchMap(products => forkJoin({
        products: of(products),
        prices: this.priceService.getCurrentCustomerPrices(productIds).pipe(first()),
        availabilities: this.includeAvailability ? this.availabilityService.getAvailabilities(productIds).pipe(first()) : of([])
      }))
    ).subscribe(({ products, prices, availabilities }) => {
      const productById = new Map(products.map(product => [product.id, product]));
      this.carouselItems = productIds
        .map(id => {
          const product = productById.get(id);
          if (!product) return null;
          const price = prices.find(p => p && p.productId === id);
          const availability = (availabilities as Availability[]).find(a => a && a.productId === id);
          const itemData: GridViewProductCardV2 = this.mapItem(product, price, availability);
          return {
            id,
            component: this.productListV2ConfigService.getProductGridCardComponent(itemData),
            data: itemData
          };
        })
        .filter(item => !!item);
      setTimeout(() => {
        this.loadingAssortment = false;
        this.carouselAlive = true;
      });
    });
  }

  private updateCarouselOptions() {
    this.carouselAlive = false;
    this.customOptions = {
      ...this.customOptions,
      responsive: {
        0: {
          items: Number(this.element.fields.itemsMobile) || 1
        },
        740: {
          items: Number(this.element.fields.itemsTablet) || 3
        },
        940: {
          items: Number(this.element.fields.itemsDesktop) || 4
        }
      }
    };

    setTimeout(() => {
      this.carouselAlive = true;
    }, 100);
  }

  private mapItem(
    product: Product,
    price: CustomerProductPrice,
    availability: Availability
  ): GridViewProductCardV2 {
    return {
      product,
      price,
      availability,
      isSales: this.isSales,
      isAnonymous: this.isAnonymous,
      includeAvailability: this.includeAvailability
    };
  }
}
